import type React from "react";
import { useCallback, useState } from "react";

import { Alert, Snackbar } from "@mui/material";

import { SnackbarContext } from "./SnackbarContext";
import type { SnackbarMessage, SnackbarOptions } from "./SnackbarContextValue";

const defaultOptions: SnackbarOptions = {
  message: "",
  autoHideDuration: 3000,
  severity: "info",
  hideClose: true,
};

export const SnackbarContextProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [snackbar, setSnackbar] = useState<SnackbarMessage>({
    id: "0",
    open: false,
    ...defaultOptions,
  });

  const showSnackbar = useCallback((options: SnackbarOptions) => {
    const id = Math.random().toString(36).substring(2);
    setSnackbar({
      id,
      open: true,
      ...defaultOptions,
      ...options,
    });
  }, []);

  const hideSnackbar = useCallback(() => {
    setSnackbar(currentSnackbar => ({
      ...currentSnackbar,
      open: false,
    }));
  }, []);

  const handleClose = (_event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === "clickaway") {
      return;
    }
    hideSnackbar();
  };

  const value = {
    showSnackbar,
    hideSnackbar,
  };

  return (
    <SnackbarContext.Provider value={value}>
      {children}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={snackbar?.autoHideDuration}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={!snackbar?.hideClose ? handleClose : undefined}
          severity={snackbar?.severity}
          sx={{ width: "100%" }}
        >
          {snackbar?.message}
        </Alert>
      </Snackbar>
    </SnackbarContext.Provider>
  );
};
